import React, { createContext, useContext, useReducer, useCallback } from 'react';
import ErrorMessage from '../components/ErrorMessage';

// Error types
export const ERROR_TYPES = {
  FILE_UPLOAD: 'FILE_UPLOAD',
  PAYMENT: 'PAYMENT',
  NETWORK: 'NETWORK',
  DATABASE: 'DATABASE',
  AUTH: 'AUTH',
  VALIDATION: 'VALIDATION',
  UNKNOWN: 'UNKNOWN'
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Initial state
const initialState = {
  errors: [],
  isGlobalErrorVisible: false,
  globalError: null
};

// Action types
const ERROR_ACTIONS = {
  ADD_ERROR: 'ADD_ERROR',
  REMOVE_ERROR: 'REMOVE_ERROR',
  CLEAR_ALL_ERRORS: 'CLEAR_ALL_ERRORS',
  SHOW_GLOBAL_ERROR: 'SHOW_GLOBAL_ERROR',
  HIDE_GLOBAL_ERROR: 'HIDE_GLOBAL_ERROR'
};

// Reducer
const errorReducer = (state, action) => {
  switch (action.type) {
    case ERROR_ACTIONS.ADD_ERROR:
      return {
        ...state,
        errors: [...state.errors, { ...action.payload, id: Date.now() }]
      };
    
    case ERROR_ACTIONS.REMOVE_ERROR:
      return {
        ...state,
        errors: state.errors.filter(error => error.id !== action.payload.id)
      };
    
    case ERROR_ACTIONS.CLEAR_ALL_ERRORS:
      return {
        ...state,
        errors: []
      };
    
    case ERROR_ACTIONS.SHOW_GLOBAL_ERROR:
      return {
        ...state,
        isGlobalErrorVisible: true,
        globalError: action.payload
      };
    
    case ERROR_ACTIONS.HIDE_GLOBAL_ERROR:
      return {
        ...state,
        isGlobalErrorVisible: false,
        globalError: null
      };
    
    default:
      return state;
  }
};

// Context
const ErrorContext = createContext();

// Provider component
export const ErrorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(errorReducer, initialState);

  // Add error
  const addError = useCallback((error) => {
    dispatch({
      type: ERROR_ACTIONS.ADD_ERROR,
      payload: {
        ...error,
        timestamp: new Date().toISOString()
      }
    });
  }, []);

  // Remove error
  const removeError = useCallback((errorId) => {
    dispatch({
      type: ERROR_ACTIONS.REMOVE_ERROR,
      payload: { id: errorId }
    });
  }, []);

  // Clear all errors
  const clearAllErrors = useCallback(() => {
    dispatch({ type: ERROR_ACTIONS.CLEAR_ALL_ERRORS });
  }, []);

  // Show global error
  const showGlobalError = useCallback((error) => {
    dispatch({
      type: ERROR_ACTIONS.SHOW_GLOBAL_ERROR,
      payload: error
    });
  }, []);

  // Hide global error
  const hideGlobalError = useCallback(() => {
    dispatch({ type: ERROR_ACTIONS.HIDE_GLOBAL_ERROR });
  }, []);

  // Helper function to create standardized errors
  const createError = useCallback((type, message, details = null, severity = ERROR_SEVERITY.MEDIUM) => {
    return {
      type,
      message,
      details,
      severity,
      timestamp: new Date().toISOString()
    };
  }, []);

  // Helper function to handle async operations with error catching
  const handleAsync = useCallback(async (asyncFn, errorType = ERROR_TYPES.UNKNOWN, errorMessage = 'An error occurred') => {
    try {
      return await asyncFn();
    } catch (error) {
      console.error('Async operation failed:', error);
      
      const errorObj = createError(
        errorType,
        errorMessage,
        error.message || error.toString(),
        ERROR_SEVERITY.MEDIUM
      );
      
      addError(errorObj);
      throw error; // Re-throw so calling code can handle if needed
    }
  }, [createError, addError]);

  // Auto-remove errors after a certain time
  const autoRemoveError = useCallback((errorId, delay = 5000) => {
    setTimeout(() => {
      removeError(errorId);
    }, delay);
  }, [removeError]);

  const value = {
    ...state,
    addError,
    removeError,
    clearAllErrors,
    showGlobalError,
    hideGlobalError,
    createError,
    handleAsync,
    autoRemoveError
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
      
      {/* Global Error Display */}
      {state.isGlobalErrorVisible && state.globalError && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <ErrorMessage
            type="error"
            title={state.globalError.title || 'Error'}
            message={state.globalError.message}
            details={state.globalError.details}
            onDismiss={hideGlobalError}
            onRetry={state.globalError.onRetry}
          />
        </div>
      )}
    </ErrorContext.Provider>
  );
};

// Hook to use error context
export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

// Higher-order component for error boundary integration
export const withErrorHandling = (WrappedComponent) => {
  return function ErrorHandledComponent(props) {
    const { addError, createError } = useError();

    const handleError = useCallback((error, errorInfo = null) => {
      const errorObj = createError(
        ERROR_TYPES.UNKNOWN,
        'An unexpected error occurred',
        errorInfo ? `${error.message}\n\nComponent Stack:\n${errorInfo.componentStack}` : error.message,
        ERROR_SEVERITY.HIGH
      );
      
      addError(errorObj);
    }, [addError, createError]);

    return <WrappedComponent {...props} onError={handleError} />;
  };
};

export default ErrorContext;
